import { useEffect } from 'react';
import { trackSignInPromptShown, trackSignInClicked } from '@/analytics';
import { isPaperThemeActive } from '@/hooks/useThemeFlag';

interface AnonSignInPromptProps {
  onSignIn: () => void;
  onContinue: () => void;
  onCancel: () => void;
}

export const AnonSignInPrompt = ({ onSignIn, onContinue, onCancel }: AnonSignInPromptProps) => {
  useEffect(() => {
    trackSignInPromptShown('anon_reset_modal');
  }, []);

  if (isPaperThemeActive()) {
    return (
      <div
        className="fixed inset-0 z-[70] flex items-center justify-center bg-[var(--paper)]/70 px-4 backdrop-blur-md"
        onClick={onCancel}
      >
        <div
          className="w-full max-w-sm rounded-2xl border border-[var(--hair)] bg-[var(--surface)] p-5 shadow-2xl"
          onClick={(event) => event.stopPropagation()}
          style={{ fontFamily: 'var(--font-sans-paper)' }}
        >
          <p className="text-[14px] font-medium text-[var(--fg)]">Start a new agent?</p>
          <p className="mt-1.5 text-[13px] leading-relaxed text-[var(--fg-3)]">
            Your current agent will be lost. Sign in to keep your agents saved.
          </p>
          <div className="mt-4 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => { trackSignInClicked('anon_reset_modal'); onSignIn(); }}
              className="inline-flex items-center justify-center h-[34px] w-full rounded-full text-[12px] font-medium tracking-[0.02em] cursor-pointer bg-[var(--fg)] text-[var(--fg-inverse)] border border-solid border-[var(--fg)] transition-colors duration-200 hover:bg-[var(--ink-3)] hover:border-[var(--ink-3)]"
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={onContinue}
              className="inline-flex items-center justify-center h-[34px] w-full rounded-full text-[12px] font-medium tracking-[0.02em] cursor-pointer bg-transparent text-[var(--fg)] border border-solid border-[var(--hair)] transition-colors duration-200 hover:bg-[var(--periwinkle-wash)] hover:text-[var(--periwinkle)] hover:border-[var(--periwinkle)]"
            >
              Continue without saving
            </button>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="mt-3 w-full text-center text-[12px] text-[var(--fg-4)] transition-colors hover:text-[var(--fg-2)]"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
  <div
    className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
    onClick={onCancel}
  >
    <div
      className="w-full max-w-sm rounded-2xl border border-white/[0.08] bg-zinc-900 p-5 shadow-2xl"
      onClick={(event) => event.stopPropagation()}
    >
      <p className="text-[13px] font-medium text-zinc-200">Start a new agent?</p>
      <p className="mt-1.5 text-[13px] leading-relaxed text-zinc-500">
        Your current agent will be lost. Sign in to keep your agents saved.
      </p>
      <div className="mt-4 flex flex-col gap-2">
        <button
          type="button"
          onClick={() => { trackSignInClicked('anon_reset_modal'); onSignIn(); }}
          className="flex h-9 w-full items-center justify-center rounded-lg bg-white/[0.1] px-4 text-[13px] font-medium text-zinc-100 transition-colors hover:bg-white/[0.16]"
        >
          Sign in
        </button>
        <button
          type="button"
          onClick={onContinue}
          className="flex h-9 w-full items-center justify-center rounded-lg px-4 text-[13px] text-zinc-400 transition-colors hover:bg-white/[0.06] hover:text-zinc-200"
        >
          Continue without saving
        </button>
      </div>
      <button
        type="button"
        onClick={onCancel}
        className="mt-3 w-full text-center text-[12px] text-zinc-600 transition-colors hover:text-zinc-400"
      >
        Cancel
      </button>
    </div>
  </div>
  );
};
